import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { useRouter } from "next/router";
import { join } from "path";
import react, { useEffect, useState } from "react";

import JsonLd from "~/components/common/json-ld";
import EditorialEntry from "~/components/editorials/editorial-entry";
import LayoutPage from "~/components/layout/layout-page";
import ProductReviews from "~/components/product/product-reviews";
import ProductShowcase from "~/components/product/product-showcase";
import { useDeferredTracking, usePageTracking } from "~/contexts/page-trackings";
import Eva from "~/services/eva";
import Logger from "~/services/logger/logger";
import styles from "~/styles/pages/product.module.scss";
import Constants from "~/utils/constants";
import { isActiveEntry } from "~/utils/editorial-utils";
import ssrUtils from "~/utils/ssr-utils";
import TrackingUtils from "~/utils/tracking-utils";

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

export default function ProductPage(props: Props) {
  const refProductReviews = react.useRef(null);

  const router = useRouter();
  const [path, setPath] = useState(router.asPath);
  const [selected, setSelected] = useState(props.selected);

  // when shallow navigating PDP (between shades) manually get data
  useEffect(() => {
    setPath(router.asPath);

    if (router.asPath !== path) {
      const currentProductId = (router.query.slug as string).split("-").at(-1)!;
      // not shallow routing
      if (Number(currentProductId) == props.selected.product_id) {
        setSelected(props.selected);
      } else {
        const eva = new Eva({ organizationUnitId: props.organizationUnitId, locale: router.locale });

        eva
          .getProductDetail(currentProductId)
          .then(async (productDetail) => {
            if (!productDetail.Result) {
              router.push(router.asPath);
              return;
            }
            const selected = productDetail.Result;
            if (props.children.length > 0) {
              if (selected.is_available === undefined) {
                const selectedAvailability = await eva.getProductAvailability([{ ID: selected.product_id }], false);

                selected.is_available = selectedAvailability?.Products[0].Delivery?.IsAvailable || false;
                selected.has_stock = selectedAvailability?.Products[0].Delivery?.HasStock || false;
              }
            }

            setSelected(selected);
          })
          .catch(() => Logger.instance.debug("product detail KO"));
      }
    }
  }, [
    path,
    props.children.length,
    props.organizationUnitId,
    props.selected,
    props.selected.product_id,
    router,
    router.locale,
    router.query.slug,
    selected,
  ]);

  usePageTracking("product", { ...TrackingUtils.mapTrackProductPage(props), freezeOn: props.root.backend_id });
  useDeferredTracking(
    TrackingUtils.mapEcommerceEvent("view_item", [TrackingUtils.mapEcomItem(props.root, selected)], {
      user_action: "detail",
    })
  );

  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: selected.display_value,
    image: selected.primary_image?.url,
    description: selected.short_description,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: selected.review_average_score?.toFixed(1),
      ratingCount: `${selected.review_total_number}`,
    },
    offers: {
      "@type": "Offer",
      price: selected.display_price,
      priceCurrency: selected.currency_id,
      availability: selected.has_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    },
  };

  return (
    <LayoutPage {...props} mainClasses={styles.productPage} productChildren={props.children} inPpd>
      <>
        <JsonLd data={JSON.stringify(productStructuredData)} />
        <ProductShowcase
          root={props.root}
          shades={props.children}
          selectedShade={selected}
          breadcrumbs={props.categories}
        />
        {props.page?.components?.filter(isActiveEntry).map((entry) => (
          <EditorialEntry key={entry.sys.id} entry={entry} />
        ))}
        <ProductReviews ref={refProductReviews} parent_backend_id={props.root.backend_id} />
      </>
    </LayoutPage>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const productId = (context.params?.slug as string).split("-").at(-1)!;

  // console.time("[PERF] Loading product page for " + context.params?.slug);
  const pageData = await ssrUtils.getServerSideProductPageData(context, context.draftMode, productId);
  // console.timeEnd("[PERF] Loading product page for " + context.params?.slug);

  if (!pageData) {
    return {
      notFound: true,
    };
  }

  if (pageData.type === "redirect_next_product") {
    let destination = join("/", context.locale!, "/p/", pageData.data.productId, "/");

    return {
      redirect: {
        destination,
        permanent: false, // TODO: make product redirects permanent
      },
    };
  }

  if (pageData.type === "redirect_category") {
    let destination = join("/", context.locale!, "/c/", pageData.data.categorySlug, "/");

    return {
      redirect: {
        destination,
        permanent: false,
      },
    };
  }

  context.res.setHeader("Cache-Control", "public, s-maxage=10, stale-while-revalidate=59");

  return {
    props: {
      ...pageData,
    },
  };
}
